<template>
  <div class="easymoney-sidebar">
    <sidebar-menu-tree v-for="option in menuOptions"
                       :elementId="option.elementId"
                       :clickFunction="option.function"
                       :label="option.label"
                       :icon="option.icon"
                       :current="option.current"
                       :display="option.display"
                       :children="option.children"
                       :key="option.elementId"
                       :notificationCount="option.notificationCount">
    </sidebar-menu-tree>
  </div>
</template>

<script>
// Components
import SidebarMenuTree from '../../../common/elements/SidebarMenuTree.vue'

// Libraries
import { mapGetters, mapState } from 'vuex'

// Utilities
import AppBrowser from '../../../../utility/appBrowserUtil'
import isEmpty from '../../../../utility/isEmpty'

// Mixins
import { sidebarMixin } from '../../../../mixins/sidebarMixin'

export default {
  name: 'easymoney-sidebar',

  components: {
    SidebarMenuTree
  },

  mixins: [sidebarMixin],

  computed: {
    multipleAccounts () {
      return this.$store.state.display.multipleAccounts
    },

    useMenuItemVisibility () {
      return !isEmpty(this.menuItemVisibility)
    },

    currentPage () {
      return this.$route.name
    },

    showMyAccounts () {
      return this.authenticated && this.currentPage !== 'Home' && this.currentPage !== `${this.navigationBrand}-Dashboard`
    },

    showManagePayMethod () {
      return this.authenticated
    },

    showReferralRewards () {
      return this.authenticated && this.isTlxeUser && this.isReferralRewardsUser && this.useMenuItemVisibility && !isEmpty(this.menuItemVisibility.referralRewardsButton) && this.menuItemVisibility.referralRewardsButton === true
    },

    showOffers () {
      return this.authenticated && this.isTlxeUser && this.useMenuItemVisibility && !isEmpty(this.menuItemVisibility.offerButton) && this.menuItemVisibility.offerButton === true
    },

    showLinkAccount () {
      return this.authenticated
    },

    showContactUs () {
      return true
    },

    showStoreLocator () {
      return true
    },

    showMyProfile () {
      return this.authenticated
    },

    showPrivacyTerms () {
      return true
    },

    showPrivacyPolicy () {
      return true
    },

    showOptInDisclosures () {
      return this.useMenuItemVisibility && !isEmpty(this.menuItemVisibility.optInDisclosuresButton) && this.menuItemVisibility.optInDisclosuresButton === true
    },

    showTermsConditions () {
      return true
    },

    menuOptions () {
      return [
        {
          elementId: 'myAccountsButton',
          function: () => { this.goAppPage(`${this.navigationBrand}-Dashboard`, { sidebarNavigate: true }) },
          label: this.multipleAccounts ? this.$t('global.accounts') : this.$t('global.account'),
          icon: 'AccountsIcon',
          current: this.currentPage === `${this.navigationBrand}-Dashboard` || this.currentPage === `${this.navigationBrand}-AccountDetails`,
          display: this.showMyAccounts
        },
        {
          elementId: 'managePaymentMethodsButton',
          function: () => { this.goAppPage(`${this.navigationBrand}-ManagePaymentMethod`) },
          label: this.$t('global.manage_payment_methods'),
          icon: 'CardIcon',
          current: this.currentPage === `${this.navigationBrand}-ManagePaymentMethod`,
          display: this.showManagePayMethod
        },
        {
          elementId: 'offersButton',
          function: () => { this.goAppPage(`${this.navigationBrand}-Offers`) },
          label: this.$t('global.offers'),
          icon: 'StarIcon',
          notificationCount: this.unviewedOffersCount,
          current: this.currentPage === `${this.navigationBrand}-Offers`,
          display: this.showOffers
        },
        {
          elementId: 'referralRewardsButton',
          function: () => { this.goAppPage('ReferralRewardsHome') },
          label: this.$t('global.referral_rewards'),
          icon: 'GiftIcon',
          current: this.currentPage === 'ReferralRewardsHome',
          display: this.showReferralRewards
        },
        {
          elementId: 'storeLocatorButton',
          function: async () => { await AppBrowser.openExternalPageInBrowser(this.brandedStoreLocatorUrl, 'Store Locator Site') },
          label: this.$t('global.store_locator'),
          icon: 'LocationPinFilledIcon',
          display: this.showStoreLocator
        },
        {
          elementId: 'contactUsButton',
          function: () => { this.goAppPage('ContactUs') },
          label: this.$t('global.contact_us'),
          icon: 'PhoneIcon',
          current: this.currentPage === 'ContactUs',
          display: this.showContactUs
        },
        {
          elementId: 'customerProfileButton',
          function: () => { this.goAppPage(`${this.navigationBrand}-CustomerProfile`) },
          label: this.$t('global.my_profile'),
          icon: 'ProfileIcon',
          current: this.currentPage === `${this.navigationBrand}-CustomerProfile`,
          display: this.showMyProfile
        },
        {
          elementId: 'privacyTermsButton',
          function: () => {},
          label: this.$t('global.privacy_terms'),
          icon: 'PrivacyIcon',
          display: this.showPrivacyTerms,
          children: [
            {
              elementId: 'termsConditionsButton',
              function: async () => { await this.goExternalPage(this.brandedTermsConditionsUrl, 'Terms and Conditions', '_system') },
              label: this.$t('global.terms_and_conditions'),
              display: this.showTermsConditions
            },
            {
              elementId: 'privacyPolicyButton',
              function: async () => { await this.goExternalPage(this.brandedPrivacyPolicyUrl, 'Privacy Notice', '_system') },
              label: this.$t('global.privacy_notice'),
              display: this.showPrivacyPolicy
            },
            {
              elementId: 'optInDisclosuresButton',
              function: () => { this.goAppPage(`${this.navigationBrand}-OptInDisclosures`) },
              label: this.$t('global.opt_in_disclosures'),
              display: this.showOptInDisclosures
            }
          ]
        },
        {
          elementId: 'uploadDocumentButton',
          function: () => { this.goAppPage(this.uploadDocumentNavigationLocation) },
          label: this.$t('global.upload_documents'),
          icon: 'FileImportIcon',
          current: this.currentPage === (this.uploadDocumentNavigationLocation),
          display: this.hasDocumentUploadAccess
        },
        {
          elementId: 'linkMyAccountButton',
          function: () => { this.goAppPage('LinkAccount') },
          label: this.$t('global.link_account'),
          icon: 'LinkIcon',
          current: this.currentPage === 'LinkAccount',
          display: this.showLinkAccount
        }
      ].filter(option => option.display)
    },

    ...mapGetters('referral', ['isReferralRewardsUser']),
    ...mapState('session', ['authenticated', 'menuItemVisibility']),
    ...mapGetters('brands', ['brandedPrivacyPolicyUrl', 'brandedStoreLocatorUrl', 'brandedTermsConditionsUrl', 'navigationBrand']),
    ...mapGetters('customer', ['isTlxeUser']),
    ...mapState('offers', ['unviewedOffersCount'])
  }
}
</script>
